/**
 * Runner-side capabilities (Protocol v3). Replaces the deleted v2
 * `AgentCommand` types `add_resource` / `remove_resource` /
 * `set_log_level` / `state_action` / lifecycle by exposing them as named
 * capabilities the platform invokes through the bidirectional
 * capability registry — the same wire pattern as agent->platform calls,
 * just in reverse.
 *
 * All seven caps are `audience: ['runtime']` so they never leak to the
 * LLM tool list or to the user-facing command palette (cmdK). They run
 * with `side: 'agent'` (handler lives in the agent runtime) and
 * `origin: { kind: 'framework' }` — `CapabilityOrigin` (as of
 * `@skaile/workspaces/types@4.0.0`) does not include a `runner` kind, and
 * per the v3 spec "origin tag stays separate (origin.kind); audience
 * determines visibility". The `runner.<verb>` naming convention lives
 * in the capability `name`, not in the origin tag.
 *
 * The factory takes a {@link RunnerCapabilityHandlers} bag so the
 * caller (typically `serve.ts`) wires each cap to its own state-
 * mutation helpers without this file having to import `serve.ts` —
 * keeping the dependency direction acyclic.
 *
 * @category Runtime
 * @since 3.0.0
 */

import type { McpServerDeclaration } from "@skaile/workspaces/core";
import { getLogStore } from "@skaile/workspaces/core/logging";
import type { CredentialMint, LogLevel } from "@skaile/workspaces/types";
import * as z from "zod";
import { type DefinedCapability, defineCapability } from "./define-capability.js";

/**
 * Per-session handlers that runner-side capabilities delegate into. The
 * caller (typically `serve.ts`) wires these to its own state-mutation
 * helpers. Each method is optional; capabilities whose handler is
 * undefined return a structured `not_implemented` error.
 *
 * @category Runtime
 * @since 3.0.0
 */
export interface RunnerCapabilityHandlers {
  /**
   * Add a mount to the live session. Implementations typically delegate
   * to `VolumeManager.mount()` after staging the `preMintedToken` into
   * the secrets provider chain.
   */
  addMount?: (input: {
    declaration: unknown;
    preMintedToken?: { token: string; expiresAt?: string | null };
  }) => Promise<{ ok: true; id: string } | { ok: false; code: string; message: string }>;

  /**
   * Add a connector to the live session. Implementations typically
   * delegate to `ConnectorManager.connect()` after staging the
   * `preMintedToken`.
   */
  addConnector?: (input: {
    declaration: unknown;
    preMintedToken?: { token: string; expiresAt?: string | null };
  }) => Promise<{ ok: true; id: string } | { ok: false; code: string; message: string }>;

  /**
   * Remove a mount or connector by id. The id namespace is shared — the
   * handler resolves whichever manager owns it.
   */
  removeResource?: (input: {
    id: string;
  }) => Promise<{ ok: true } | { ok: false; code: string; message: string }>;

  /**
   * Replace the pre-minted credential on an existing mount or
   * connector. Used for proactive rotation before TTL expiry, so the
   * resource can keep running without a reconnect.
   */
  updateCredential?: (input: {
    kind: "mount" | "connector";
    id: string;
    mint: CredentialMint;
  }) => Promise<{ ok: true } | { ok: false; code: string; message: string }>;

  /**
   * Set the in-container LogStore minimum level. When omitted, the
   * cap falls back to calling `getLogStore()?.setLevel(level)` itself
   * (the default behaviour matches the legacy `set_log_level` command).
   */
  setLogLevel?: (input: { level: string }) => Promise<{ ok: true }>;

  /**
   * Write into the shared state store. Shape matches the legacy
   * `state_action` v2 command body so existing platform-side senders
   * can be migrated mechanically.
   */
  setState?: (input: { store: string; key: string; value: unknown }) => Promise<{ ok: true }>;

  /** Trigger a session lifecycle transition. */
  lifecycle?: (input: { phase: "hibernate" | "close" | "compact" }) => Promise<{ ok: true }>;

  /**
   * Live-attach a remote MCP server to the running session. Stages the
   * pre-minted bearer then delegates to `ExternalMcpManager.addServer`.
   */
  addMcpServer?: (input: {
    declaration: McpServerDeclaration;
    preMintedToken?: { token: string; expiresAt?: string | null };
  }) => Promise<
    | { ok: true; id: string; live: boolean; toolCount: number }
    | { ok: false; code: string; message: string }
  >;

  /**
   * Materialize-then-attach-by-ref: the platform writes the asset to
   * `.skaile/assets/mcp-server/<name>/` then invokes this cap; the runner
   * self-derives the declaration from disk and attaches it live.
   */
  attachInstance?: (input: {
    kind: string;
    name: string;
    preMintedToken?: { token: string; expiresAt?: string | null };
  }) => Promise<
    | { ok: true; id: string; live: boolean; toolCount: number }
    | { ok: false; code: string; message: string }
  >;
}

/** Common origin tag used by all runner-side caps. */
const RUNNER_CAP_ORIGIN = { kind: "framework" } as const;

/**
 * Build the standard `not_implemented` reply for a cap whose handler
 * was not wired by the caller. The shape mirrors the success/error
 * union the wired handlers return, so callers consuming the result
 * uniformly only need one discriminator (`ok`).
 */
function notImplemented(name: string): { ok: false; code: "not_implemented"; message: string } {
  return {
    ok: false,
    code: "not_implemented",
    message: `${name} handler not configured`,
  };
}

// Input schemas. `declaration` and `value` use `z.unknown()` with an
// explicit `.refine(() => true)` so the inferred type is `unknown`
// (required) rather than `unknown | undefined` (optional) — Zod's
// default treatment of `z.unknown()` is optional even at the top of
// an object, which would break the handler-input contract.
const requiredUnknown = z.unknown().refine(() => true);

const preMintedTokenSchema = z
  .object({
    token: z.string(),
    expiresAt: z.string().nullable().optional(),
  })
  .optional();

const mountDeclarationInput = z.strictObject({
  declaration: requiredUnknown,
  preMintedToken: preMintedTokenSchema,
  // The platform attaches a workspace-migration directive on add_mount; the
  // handler ignores it for now (replace-workspace hot-migration is a follow-up).
  // Accepted here so a real platform add_mount payload validates.
  migration: z.unknown().optional(),
});

const connectorDeclarationInput = z.strictObject({
  declaration: requiredUnknown,
  preMintedToken: preMintedTokenSchema,
});

/**
 * Wire-contract input schema for `runner.add_mcp_server`. Exported so the
 * wire-contract fixture can assert the shape without importing the handler.
 */
export const addMcpServerInputSchema = z.strictObject({
  declaration: requiredUnknown,
  preMintedToken: preMintedTokenSchema,
});

/**
 * Wire-contract input schema for `runner.attach_instance`. Exported so the
 * wire-contract fixture can assert the shape without importing the handler.
 */
export const attachInstanceInputSchema = z.strictObject({
  kind: z.string(),
  name: z.string(),
  preMintedToken: preMintedTokenSchema,
});

const removeResourceInput = z.strictObject({
  id: z.string(),
});

const updateCredentialInput = z.strictObject({
  kind: z.enum(["mount", "connector"]),
  id: z.string(),
  // `CredentialMint` is a discriminated union from `@skaile/workspaces/types`.
  // We accept it as `z.unknown()` here because Zod can't easily mirror
  // a discriminated TypeScript union without duplicating the schema —
  // the platform side has already validated the shape before sending.
  mint: requiredUnknown,
});

const setLogLevelInput = z.strictObject({
  level: z.enum(["silent", "error", "warn", "info", "debug", "trace"]),
});

const setStateInput = z.strictObject({
  store: z.string(),
  key: z.string(),
  value: requiredUnknown,
});

// Canonical session-lifecycle contract. The platform invokes `runner.lifecycle`
// with exactly this `{ phase }` shape (5 sites in the platform's
// session-lifecycle.service.ts). `compact` is the platform-driven compaction
// signal (manual + pre-hibernate); `shutdown` was never sent and is dropped.
const lifecycleInput = z.strictObject({
  phase: z.enum(["hibernate", "close", "compact"]),
});

/** Levels accepted by the underlying `LogStore.setLevel`. */
const VALID_LOG_STORE_LEVELS = new Set<LogLevel>(["debug", "info", "warn", "error"]);

/**
 * Build the seven `runner.*` capabilities. Caller registers each with
 * `capabilityRegistry.register(cap, "agent")`.
 *
 * The returned capabilities are independent — the caller can also
 * register a subset, e.g. for a stub runner used in tests.
 *
 * @category Runtime
 * @since 3.0.0
 */
export function buildRunnerCapabilities(handlers: RunnerCapabilityHandlers): DefinedCapability[] {
  return [
    defineCapability({
      name: "runner.add_mount",
      displayName: "Add mount",
      description:
        "Mount a volume into the live session. Used by the platform to attach data sources mid-session.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: mountDeclarationInput,
      handler: async (input) => {
        if (!handlers.addMount) return notImplemented("runner.add_mount");
        return handlers.addMount({
          declaration: input.declaration,
          preMintedToken: input.preMintedToken,
        });
      },
    }),
    defineCapability({
      name: "runner.add_connector",
      displayName: "Add connector",
      description:
        "Connect a tool-providing connector into the live session. Used by the platform to attach databases, APIs, etc. mid-session.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: connectorDeclarationInput,
      handler: async (input) => {
        if (!handlers.addConnector) return notImplemented("runner.add_connector");
        return handlers.addConnector({
          declaration: input.declaration,
          preMintedToken: input.preMintedToken,
        });
      },
    }),
    // TODO(mcp-detach): no runner.remove_mcp_server yet — live MCP detach + mcp: credential cleanup deferred with the attach-by-ref rework.
    defineCapability({
      name: "runner.add_mcp_server",
      displayName: "Add MCP server",
      description:
        "Live-attach a remote MCP server to the running session. Used by the platform to connect additional MCP tool servers mid-session.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: addMcpServerInputSchema,
      handler: async (input) => {
        if (!handlers.addMcpServer) return notImplemented("runner.add_mcp_server");
        // `requiredUnknown` keeps the wire schema dep-free; the handler narrows.
        return handlers.addMcpServer({
          declaration: input.declaration as McpServerDeclaration,
          preMintedToken: input.preMintedToken,
        });
      },
    }),
    defineCapability({
      name: "runner.attach_instance",
      displayName: "Attach instance",
      description:
        "Materialize-then-attach-by-ref: the platform writes the asset to .skaile/assets/ then invokes this cap; the runner self-derives the declaration from disk and attaches it live.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: attachInstanceInputSchema,
      handler: async (input) => {
        if (!handlers.attachInstance) return notImplemented("runner.attach_instance");
        return handlers.attachInstance({
          kind: input.kind,
          name: input.name,
          preMintedToken: input.preMintedToken,
        });
      },
    }),
    defineCapability({
      name: "runner.remove_resource",
      displayName: "Remove resource",
      description:
        "Detach a mount or connector by id. The id namespace is shared across both kinds.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: removeResourceInput,
      handler: async (input) => {
        if (!handlers.removeResource) return notImplemented("runner.remove_resource");
        return handlers.removeResource(input);
      },
    }),
    defineCapability({
      name: "runner.update_credential",
      displayName: "Update credential",
      description:
        "Replace the pre-minted credential on an existing mount or connector. Used for proactive rotation before TTL expiry.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: updateCredentialInput,
      handler: async (input) => {
        if (!handlers.updateCredential) return notImplemented("runner.update_credential");
        return handlers.updateCredential({
          kind: input.kind,
          id: input.id,
          mint: input.mint as CredentialMint,
        });
      },
    }),
    defineCapability({
      name: "runner.set_log_level",
      displayName: "Set log level",
      description:
        "Live-update the in-container LogStore minimum level without restarting the session.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: setLogLevelInput,
      handler: async (input) => {
        if (handlers.setLogLevel) {
          return handlers.setLogLevel(input);
        }
        // Default behaviour matches the legacy `set_log_level` command:
        // adjust the process-singleton LogStore directly. The cap input
        // accepts 6 levels per the v3 spec for forward-compatibility with
        // `silent` / `trace`, but `LogStore.setLevel` only knows the four
        // canonical ones — drop unknown values silently.
        const candidate = input.level as LogLevel;
        if (VALID_LOG_STORE_LEVELS.has(candidate)) {
          getLogStore()?.setLevel(candidate);
        }
        return { ok: true };
      },
    }),
    defineCapability({
      name: "runner.set_state",
      displayName: "Set state",
      description:
        "Write into the session shared state store. Shape matches the legacy state_action command body.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: setStateInput,
      handler: async (input) => {
        if (!handlers.setState) {
          // Honour the protocol contract by returning a structured
          // not_implemented error; callers can detect the gap without
          // a thrown exception. Cast through unknown because the cap's
          // declared output is `{ ok: true }` per the legacy shape.
          return notImplemented("runner.set_state") as unknown as { ok: true };
        }
        return handlers.setState({
          store: input.store,
          key: input.key,
          value: input.value,
        });
      },
    }),
    defineCapability({
      name: "runner.lifecycle",
      displayName: "Session lifecycle",
      description:
        "Transition the session lifecycle: hibernate (connectors persist state), close (connectors finalize), or compact conversation history.",
      side: "agent",
      origin: RUNNER_CAP_ORIGIN,
      audience: ["runtime"],
      kind: "effect",
      input: lifecycleInput,
      handler: async (input) => {
        if (!handlers.lifecycle) {
          return notImplemented("runner.lifecycle") as unknown as { ok: true };
        }
        return handlers.lifecycle(input);
      },
    }),
  ];
}
